import {
  compose,
  curry,
  is,
  prop,
  slice,
  toLower,
  toUpper,
  split,
  reduce,
  defaultTo,
} from 'ramda';

export const replace = curry((pattern, replacer, str) =>
  str.replace(pattern, replacer)
);

const firstToUpper = compose(toUpper, prop(0));
const allButFirst = slice(1, Infinity);

/**
 * @param {(String|Array)} first
 * @param {...(String|Array)} args
 */
export const concat = (first, ...args) => first.concat(...args);

/**
 * @param {(String|Array)} first
 * @return {function(...(String|Array))}
 */
export const prefix = (first) => (...args) => first.concat(...args);

/**
 * @param {String} str - a kebabized string to camelize
 * @returns {String}
 */
export const camelize = (str) => {
  const res = replace(/[-_][a-z]/g, compose(toUpper, prop(1)), str);
  return concat(firstToUpper(res), allButFirst(res));
};

/**
 * @param {String} str - a camelized string to kebabize
 * @returns {String}
 */
export const kebabize = compose(
  toLower,
  replace(/[A-Z]/g, (str, i) => `${(i && '-') || ''}${str}`)
);

/**
 * @param {String|RegExp} part
 * @param {String} str
 * @returns {String}
 */
export const remove = curry((part, str) =>
  replace(part, '', defaultTo('', str))
);

export const clean = (str) => (is(String, str) ? str : '');

export const ascii = compose(
  replace(/[áàâä]/g, 'a'),
  replace(/[éèêë]/g, 'e'),
  replace(/[íìîï]/g, 'i'),
  replace(/[óòôö]/g, 'o'),
  replace(/[úùûü]/g, 'u'),
  replace(/ç/g, 'c'),
  replace(/ñ/g, 'n'),
  replace(/œ/g, 'oe'),
  replace(/æ/g, 'ae')
);

export const sanitizeString = compose(ascii, toLower);

export const trim = remove(/^\s+|\s+$/g);

export const match = curry((reg, str) => str.match(reg));

export const firstMatch = (r) => compose(prop(1), split(r));

export const generateHashFrom = compose(
  (hash) => hash.substr(2, 5),
  (hash) => hash.toString(36),
  reduce((acc, value) => {
    const hash = (acc << 5) - acc + value.charCodeAt(0);
    return hash & hash;
  }, 0),
  defaultTo('')
);
